/**
 * AutosaveController — Lit ReactiveController encapsulating the
 * debounced autosave path: schedule a save N ms after the last
 * session event, write to localStorage keyed by campaign, and warn
 * (then refuse) once the JSON gets large.
 *
 * Extracted from `src/quire-app.ts` during M1 (P0-9).  The host
 * (QuireApp) supplies a `buildDoc()` callback that constructs the
 * save document from the current session + campaign.  The controller
 * owns the timer, quota-warned flag, and the localStorage key
 * convention.
 *
 * The matching resume-prompt flow is in `checkResume()` — it looks
 * for a prior autosave for a given campaign and returns the parsed
 * SaveDocument (or null).  The host displays / dismisses the resume
 * UI by mutating its own @state — that's a render concern.
 *
 * Caps:
 *   SAVE_AUTOSAVE_DEBOUNCE_MS = 1500   (debounce window per change)
 *   SAVE_QUOTA_WARN_BYTES     = 1_000_000  (warn once at 1 MB)
 *   SAVE_QUOTA_REFUSE_BYTES   = 4_000_000  (refuse autosave > 4 MB)
 *
 * Refuse is silent for autosave; the user can still trigger a manual
 * "Save to file" via the existing UI.
 */

import type { ReactiveController, ReactiveControllerHost } from 'lit';
import {
  stringifySave,
  parseSaveDocument,
  type SaveDocument
} from '../persistence';

export const SAVE_STORAGE_PREFIX = 'quire.save.';
export const SAVE_AUTOSAVE_DEBOUNCE_MS = 1500;
export const SAVE_QUOTA_WARN_BYTES = 1_000_000;
export const SAVE_QUOTA_REFUSE_BYTES = 4_000_000;

export interface CampaignRefLike {
  owner: string;
  repo: string;
}

function storageKey(campaign: CampaignRefLike): string {
  return `${SAVE_STORAGE_PREFIX}${campaign.owner}-${campaign.repo}`;
}

export class AutosaveController implements ReactiveController {
  private timer: ReturnType<typeof setTimeout> | null = null;
  private quotaWarned: boolean = false;
  /**
   * M5 (2026-05-29 save-restore program): one-shot sticky flag for
   * `navigator.storage.persist()`. We ask the browser to mark our
   * origin as persistent the first time we successfully write a save
   * for a campaign. The API is idempotent (the browser keeps the
   * flag once granted), but the prompt is intrusive on some
   * browsers so we ONLY ask after the first successful save — at
   * which point the user has demonstrated they're committed enough
   * to lose data over.
   *
   * Per WHATWG Storage spec: `persist()` resolves to `true` when the
   * browser grants persistence, `false` when it doesn't (e.g. third-
   * party context, user denied, browser policy). Failure is non-
   * fatal — we just don't get the eviction protection.
   *
   * The sticky-flag is in-memory only. If the user reloads the page,
   * we'll ask again — but `persist()` is a no-op if already granted,
   * so this is cheap.
   */
  private persistRequested: boolean = false;
  /**
   * M2 (2026-05-29 save-restore program): bound visibilitychange
   * handler so we can `removeEventListener` on host-disconnect.  The
   * arrow form captures `this`; storing the ref means add/remove
   * pair use the same identity.
   */
  private readonly onVisibilityChange = (): void => {
    if (typeof document === 'undefined') return;
    if (document.visibilityState !== 'hidden') return;
    // Only do work when we have something in flight.  No-op when the
    // user has just navigated and there's no pending save.
    if (this.timer === null) return;
    // Flush synchronously.  performNow() handles the build/quota/etc;
    // we clear the pending timer first so it doesn't fire again.
    this.cancelPending();
    this.performNow();
  };

  constructor(
    host: ReactiveControllerHost,
    private readonly buildDoc: () => SaveDocument | null
  ) {
    host.addController(this);
  }

  hostConnected(): void {
    // M2: register the tab-close-flush listener.  We use
    // `visibilitychange` rather than `beforeunload` because:
    //   - `beforeunload` is suppressed on mobile Safari and during
    //     `pagehide`-triggered bfcache eviction.
    //   - `visibilitychange → hidden` fires reliably across
    //     desktop + mobile and is THE recommended signal per
    //     WHATWG / Page Lifecycle API.
    //   - Synchronous `localStorage.setItem` inside a
    //     visibilitychange handler is durable (the browser has not
    //     yet released the page).
    // The `pagehide` event is also fired but ONLY on real unloads;
    // visibilitychange fires on background-tab too, which is the
    // common DM case (switching to GitHub to look at scene markdown).
    // Saving aggressively on tab-background isn't a problem — it's a
    // single localStorage write debounced by `this.timer === null`.
    if (typeof document !== 'undefined' && document.addEventListener) {
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    }
  }

  hostDisconnected(): void {
    // Cancel-on-route-change is intentional: a half-typed save
    // shouldn't fire as the user navigates between campaigns.  The
    // visibilitychange listener handles the tab-close case
    // independently (it fires BEFORE hostDisconnected on tab-close,
    // so the flush has already happened).
    if (typeof document !== 'undefined' && document.removeEventListener) {
      document.removeEventListener(
        'visibilitychange',
        this.onVisibilityChange
      );
    }
    this.cancelPending();
  }

  /**
   * Debounced schedule.  Subsequent calls within the debounce window
   * reset the timer.  Safe to call from a session-state subscribe
   * callback on every event.
   */
  schedule(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.timer = null;
      this.performNow();
    }, SAVE_AUTOSAVE_DEBOUNCE_MS);
  }

  /**
   * Cancel any pending debounced save.  Useful when leaving a
   * session or unmounting — we don't want the timer firing after.
   */
  cancelPending(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  /**
   * Run the autosave immediately.  No-op when buildDoc returns null
   * (no active session / no campaign).  Refuses silently above the
   * REFUSE cap; warns once above the WARN cap.
   */
  performNow(): void {
    const doc = this.buildDoc();
    if (!doc) return;
    const json = stringifySave(doc);
    if (json.length > SAVE_QUOTA_REFUSE_BYTES) {
      // Refuse — user can still download via Save button.
      this.quotaWarned = true;
      return;
    }
    if (json.length > SAVE_QUOTA_WARN_BYTES && !this.quotaWarned) {
      console.warn(
        `[quire] autosave is large (${(json.length / 1000).toFixed(0)}KB); consider downloading a manual save.`
      );
      this.quotaWarned = true;
    }
    try {
      window.localStorage?.setItem(storageKey(doc.campaign), json);
      // M5: after the first successful save, ask the browser to mark
      // this origin's storage as persistent — protects the autosave
      // from being silently evicted under storage pressure (TTRPG-UX
      // finding 1). One-shot; subsequent saves don't re-prompt.
      this.requestPersistentStorage();
    } catch {
      // QuotaExceededError, sandboxed contexts, etc.  Non-fatal.
    }
  }

  /**
   * M5: best-effort request for persistent storage. The browser may
   * prompt the user, silently grant, or silently deny. Either way the
   * result is non-fatal — the autosave still works without the flag,
   * but is eligible for eviction.
   *
   * Idempotent: only fires once per controller lifetime. The browser
   * itself remembers persistence-granted across reloads, so this is
   * cheap on re-runs.
   */
  private requestPersistentStorage(): void {
    if (this.persistRequested) return;
    this.persistRequested = true;
    if (typeof navigator === 'undefined') return;
    const storage = navigator.storage;
    if (!storage || typeof storage.persist !== 'function') return;
    // Fire-and-forget; we don't surface the outcome to the user.
    // Result-handling left in for future surfacing in the DM-only
    // operational view (per ux-strategy.md DM-only).
    try {
      void storage.persist().catch(() => {
        /* swallowed — non-fatal */
      });
    } catch {
      /* swallowed — non-fatal */
    }
  }

  /**
   * Look up a prior autosave for the given campaign.  Returns the
   * parsed SaveDocument or null (no save / parse failure /
   * localStorage unavailable).
   */
  checkResume(campaign: CampaignRefLike): SaveDocument | null {
    try {
      const json = window.localStorage?.getItem(storageKey(campaign));
      if (!json) return null;
      const parsed = parseSaveDocument(json);
      return parsed.ok ? parsed.doc : null;
    } catch {
      return null;
    }
  }

  /**
   * Reset the "warned about size" sticky flag.  Useful after the
   * user manually trims their session via Reclaim/truncate, where
   * the next autosave may be small again.
   */
  resetQuotaWarning(): void {
    this.quotaWarned = false;
  }
}
